import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { debounce, isEmpty } from 'underscore';
import classNames from 'classnames';
import { fromJS, List } from 'immutable';
import moment from 'moment';

import { downloadBrowseFile } from 'src/utils/downloadFile';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import * as browseActionCreators from '../../actions/browse';
import config from '../../config';
import { BrowseCheckbox } from '../../components/Browse/BrowseCheckbox';
import { BrowseControlContent } from '../../components/Browse/BrowseControlContent';
import BrowseInputTag from '../../components/Browse/BrowseInputTag';
import { BrowseAutoCompleteTag } from '../../components/Browse/BrowseAutoCompleteTag';
import { BrowseDropDown } from '../../components/Browse/BrowseDropDown';
import BrowseTable from '../../components/Browse/BrowseTable';
import { BrowseActionFilter } from '../../components/Browse/BrowseActionFilter';
import { BrowseActivityFilter } from '../../components/Browse/BrowseActivityFilter';
import { BrowseFundFilter } from '../../components/Browse/BrowseFundFilter';
import {
  buildRangeParam,
  buildDateParam,
  buildTagParam,
  buildActionParam,
  buildActivityParam,
  buildSortByParam,
  buildDropdownParam,
  buildCheckboxArrayParam,
  buildTagObjectParam,
} from '../../helpers/paramBuilder';
import { fetchExecutivesStatuses } from '../../actions/statuses';
import { fetchEventActivities } from '../../actions/eventActivities';
import { BrowseStatusesFilter } from '../../components/Browse/BrowseStatusesFilter';
import { showError } from '../../utils/MessagePopup';
import { checkBrowsePageAccess } from '../../utils/checkPermissions';
import { usePopupContext } from '../../contexts/PopupContext';
import { Card } from '../../components/helpers/Card';

import style from './style.module.css';

const SAFE_EXPORT_EXECUTIVES_NUMBER = 300;

const executivesFilter = config.browse.getIn(['filter', 'executives']);
const columnDefs = config.browse.getIn(['table', 'columnDefs', 'executives']);
const states = fromJS(config.company.states.map(st => ({ name: st, value: st })));
const contextActionList = config.browse.getIn(['contextActionList', 'executives']);

/**
 * Prepare moment() date object into ISO string.
 *
 * @param data Action data.
 */
const prepareActionData = data => ({
  ...data,
  endDate: moment.isMoment(data.endDate) ? data.endDate.toISOString() : data.endDate,
  startDate: moment.isMoment(data.startDate) ? data.startDate.toISOString() : data.startDate,
});

/**
 * Build all state data to restore it on a getting client.
 *
 * @param {object} filterState Executives filter state.
 */
const buildFiltersStateParam = filterState => {
  const stateParams = {};

  // Collect needed filters if it's selected.
  if (filterState.getIn(['currentStatus', 'checked']))
    stateParams.currentStatus = filterState.get('currentStatus').toJS();
  if (filterState.getIn(['highStatus', 'checked'])) stateParams.highStatus = filterState.get('highStatus').toJS();
  if (filterState.getIn(['module', 'checked'])) stateParams.module = filterState.get('module').toJS();
  if (filterState.getIn(['analyst', 'checked'])) stateParams.analyst = filterState.get('analyst').toJS();
  if (filterState.getIn(['dealmaker', 'checked'])) stateParams.dealmaker = filterState.get('dealmaker').toJS();
  if (filterState.getIn(['researcher', 'checked'])) stateParams.researcher = filterState.get('researcher').toJS();
  if (filterState.getIn(['fund', 'checked'])) stateParams.fund = filterState.get('fund').toJS();
  if (filterState.getIn(['harvcoTags', 'checked'])) stateParams.harvcoTags = filterState.get('harvcoTags').toJS();
  if (filterState.getIn(['industry', 'checked'])) stateParams.industry = filterState.get('industry').toJS();
  if (filterState.getIn(['nextAction', 'checked'])) {
    stateParams.nextAction = prepareActionData(filterState.get('nextAction').toJS());
  }

  if (filterState.getIn(['activity', 'checked'])) {
    stateParams.activity = prepareActionData(filterState.get('activity').toJS());
  }
  if (filterState.getIn(['areaCode', 'checked'])) stateParams.areaCode = filterState.get('areaCode').toJS();
  if (filterState.getIn(['companyState', 'checked'])) stateParams.companyState = filterState.get('companyState').toJS();
  if (filterState.getIn(['keywords', 'checked'])) stateParams.keywords = filterState.get('keywords').toJS();
  if (filterState.getIn(['resume', 'checked'])) stateParams.resume = filterState.get('resume').toJS();
  if (filterState.getIn(['mbo', 'checked'])) stateParams.mbo = filterState.get('mbo').toJS();
  if (filterState.getIn(['corpBuyer', 'checked'])) stateParams.corpBuyer = filterState.get('corpBuyer').toJS();
  if (filterState.getIn(['needsFund', 'checked'])) stateParams.needsFund = filterState.get('needsFund').toJS();
  if (filterState.getIn(['peExperience', 'checked'])) stateParams.peExperience = filterState.get('peExperience').toJS();
  if (filterState.getIn(['events', 'checked'])) stateParams.events = filterState.get('events').toJS();
  if (filterState.getIn(['meet', 'checked'])) stateParams.meet = filterState.get('meet').toJS();
  if (filterState.getIn(['quality', 'checked'])) stateParams.quality = filterState.get('quality').toJS();
  if (filterState.getIn(['interest', 'checked'])) stateParams.interest = filterState.get('interest').toJS();
  if (filterState.getIn(['relationship', 'checked'])) stateParams.relationship = filterState.get('relationship').toJS();
  if (filterState.getIn(['doNotMail', 'checked'])) stateParams.doNotMail = filterState.get('doNotMail').toJS();
  if (filterState.getIn(['employed', 'checked'])) stateParams.employed = filterState.get('employed').toJS();

  return stateParams;
};

const buildRequestParam = filterState => {
  const requestParams = {
    ...buildRangeParam(filterState, 'current_status_range', 'currentStatus'),
    ...buildRangeParam(filterState, 'high_status_range', 'highStatus'),
    ...buildTagParam(filterState, 'directors', 'module', 'id'),
    ...buildTagParam(filterState, 'analysts', 'analyst', 'id'),
    ...buildTagParam(filterState, 'dealmakers', 'dealmaker', 'id'),
    ...buildTagParam(filterState, 'researchers', 'researcher', 'id'),
    ...buildTagParam(filterState, 'tags', 'harvcoTags', 'value'),
    ...buildTagParam(filterState, 'industries', 'industry', 'value'),
    ...buildDateParam(filterState, 'next_action_dates', 'nextAction'),
    ...buildActionParam(filterState, 'next_actions', 'nextAction'),
    ...buildTagParam(filterState, 'area_codes', 'areaCode', 'value'),
    ...buildTagParam(filterState, 'states', 'companyState', 'value'),
    ...buildTagParam(filterState, 'keywords', 'keywords', 'value'),
    ...buildDropdownParam(filterState, 'resume', 'resume'),
    ...buildDropdownParam(filterState, 'mbo', 'mbo'),
    ...buildDropdownParam(filterState, 'corp_buyer', 'corpBuyer'),
    ...buildDropdownParam(filterState, 'needs_fund', 'needsFund'),
    ...buildDropdownParam(filterState, 'pe_experience', 'peExperience'),
    ...buildDropdownParam(filterState, 'events', 'events'),
    ...buildDropdownParam(filterState, 'meet', 'meet'),
    ...buildCheckboxArrayParam(filterState, 'quality', 'quality', 'value'),
    ...buildCheckboxArrayParam(filterState, 'interest', 'interest', 'value'),
    ...buildCheckboxArrayParam(filterState, 'relationship', 'relationship', 'value'),
    ...buildDropdownParam(filterState, 'do_not_mail', 'doNotMail'),
    ...buildDropdownParam(filterState, 'employed', 'employed'),
  };

  if (filterState.getIn(['activity', 'lastActivity'])) {
    Object.assign(requestParams, buildDateParam(filterState, 'last_action_dates', 'activity'));
    Object.assign(requestParams, buildTagObjectParam(filterState, 'last_actions', 'activity'));
  } else {
    Object.assign(requestParams, buildActivityParam(filterState, 'activity', 'activity'));
  }

  if (Array.isArray(requestParams.activity)) {
    const activityUsers = buildTagParam(filterState, 'users', 'activity.users', 'id');
    const activityBuyers = buildTagParam(filterState, 'buyers', 'activity.buyers', 'id');
    const eventActivities = buildTagObjectParam(filterState, 'activities', 'activity', 'id');

    if (activityUsers) {
      requestParams.activity.push({
        users: activityUsers.users,
      });
    }

    if (activityBuyers) {
      requestParams.activity.push({
        buyers: activityBuyers.buyers,
      });
    }

    if (eventActivities) {
      requestParams.activity.forEach(activityItem => {
        if (activityItem.activities) {
          // eslint-disable-next-line no-param-reassign
          activityItem.activities = eventActivities.activities;
        }
      });
    }
  }

  if (filterState.getIn(['activity', 'projectType', 'checked'])) {
    if (Array.isArray(requestParams.activity)) {
      const { activity } = requestParams;

      activity.push({
        project_type: filterState.getIn(['activity', 'projectType', 'selected', 'value']),
      });
      Object.assign(requestParams, { activity });
    }
  }

  if (filterState.getIn(['activity', 'statusChanged'])) {
    if (Array.isArray(requestParams.activity)) {
      const statusChangedFromValues = filterState.getIn(['activity', 'statusChangedFrom', 'text']).toJS();
      const statusChangedToValues = filterState.getIn(['activity', 'statusChangedTo', 'text']).toJS();
      const activity = requestParams.activity.slice();

      activity.push({
        status_changed_from: {
          min: statusChangedFromValues.min,
          max: statusChangedFromValues.max,
        },
      });
      activity.push({
        status_changed_to: {
          min: statusChangedToValues.min,
          max: statusChangedToValues.max,
        },
      });
      Object.assign(requestParams, { activity });
    }
  }

  if (filterState.getIn(['fund', 'checked'])) {
    const selectedListParam = filterState.getIn(['fund', 'selectedListParam']);

    if (selectedListParam.size > 0) {
      requestParams.funds = selectedListParam.toJS();
    }
  }

  return requestParams;
};

export const BrowseExecutives = props => {
  const {
    activities,
    allActivitiesWithoutDuplicates,
    executives,
    handleAbortRequest,
    handleBrowse,
    handleClearFilter,
    handleExecutivesAddTag,
    handleExportWithSearchCriteria,
    handleFetchCriteria,
    handleGetBuyerSuggestion,
    handleGetFundProjectSuggestion,
    handleGetFundSuggestion,
    handleGetModuleSuggestion,
    handleGetUsersSuggestion,
    handleSaveAddUpdateBuyer,
    handleSearchParamsBeforeBrowse,
    handleTargetDelete,
    handleTargetPushNextActionsDates,
    handleTargetSetNextActions,
    handleUpdateExecutivesActivityFilter,
    handleUpdateExecutivesFundFilter,
    handleUpdateFilter,
    handleUpdateQueryResult,
    location,
    massSelectRows,
    onFetchEventActivities,
    onFetchStatuses,
    setSearchParamsHash,
    statuses,
    targets,
    updateTabVisibility,
    user,
    ...rest
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const keepSelectionRef = useRef(false);
  const hasMountedRef = useRef(false);
  const previousSortModelRef = useRef(executives.getIn(['sortBy', 'sortModel']));
  // Bundles every prop a debounced handler reads live, per [[feedback-minimize-useref]] - debounced
  // handlers can't use the re-subscription trick since that would reset their debounce timers.
  const liveRef = useRef({ executives, activities, targets, allActivitiesWithoutDuplicates });

  liveRef.current = { executives, activities, targets, allActivitiesWithoutDuplicates };

  const hasPageAccess = checkBrowsePageAccess(user, 'executives');

  const handleChange = useCallback(
    data => {
      if (data.filterName === 'sortBy') {
        keepSelectionRef.current = true;
      }

      const extra = data.key ? { key: data.key } : {};

      handleUpdateFilter({
        ...extra,
        name: 'executives',
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [handleUpdateFilter],
  );

  const handleGetNextPageData = useCallback(
    (page, keepSelection = true) => {
      const params = buildRequestParam(executives);
      const sortBy = buildSortByParam(executives);

      handleSearchParamsBeforeBrowse({
        params: {
          requestParams: params,
          stateParams: buildFiltersStateParam(executives),
        },
        name: 'executives',
        afterSuccess: () => {
          handleBrowse({
            name: 'executives',
            params,
            page,
            sortBy,
            keepSelection,
          });
        },
      });
    },
    [executives, handleSearchParamsBeforeBrowse, handleBrowse],
  );

  const fetchSuggestionsForModule = useCallback(
    ({ value }) => {
      handleGetModuleSuggestion('executives', value);
    },
    [handleGetModuleSuggestion],
  );
  const handleGetSuggestionForModule = useMemo(() => debounce(fetchSuggestionsForModule, 200), [
    fetchSuggestionsForModule,
  ]);

  const fetchNextSuggestionsForModule = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['module', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetModuleSuggestion('executives', value, currentPage + 1);
      }
    },
    [handleGetModuleSuggestion],
  );
  const handleGetNextSuggestionForModule = useMemo(() => debounce(fetchNextSuggestionsForModule, 100), [
    fetchNextSuggestionsForModule,
  ]);

  const fetchSuggestionsForAnalyst = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('executives', 'analyst', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForAnalyst = useMemo(() => debounce(fetchSuggestionsForAnalyst, 200), [
    fetchSuggestionsForAnalyst,
  ]);

  const fetchNextSuggestionsForAnalyst = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['analyst', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('executives', 'analyst', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForAnalyst = useMemo(() => debounce(fetchNextSuggestionsForAnalyst, 100), [
    fetchNextSuggestionsForAnalyst,
  ]);

  const fetchSuggestionsForDealmaker = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('executives', 'dealmaker', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForDealmaker = useMemo(() => debounce(fetchSuggestionsForDealmaker, 200), [
    fetchSuggestionsForDealmaker,
  ]);

  const fetchNextSuggestionsForDealmaker = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['dealmaker', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('executives', 'dealmaker', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForDealmaker = useMemo(() => debounce(fetchNextSuggestionsForDealmaker, 100), [
    fetchNextSuggestionsForDealmaker,
  ]);

  const fetchSuggestionsForResearcher = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('executives', 'researcher', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForResearcher = useMemo(() => debounce(fetchSuggestionsForResearcher, 200), [
    fetchSuggestionsForResearcher,
  ]);

  const fetchNextSuggestionsForResearcher = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['researcher', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('executives', 'researcher', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForResearcher = useMemo(() => debounce(fetchNextSuggestionsForResearcher, 100), [
    fetchNextSuggestionsForResearcher,
  ]);

  const fetchSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('executives.activity', 'users', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForActivityUser = useMemo(() => debounce(fetchSuggestionsForActivityUser, 200), [
    fetchSuggestionsForActivityUser,
  ]);

  const fetchNextSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['activity', 'users', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('executives.activity', 'users', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForActivityUser = useMemo(
    () => debounce(fetchNextSuggestionsForActivityUser, 200),
    [fetchNextSuggestionsForActivityUser],
  );

  const fetchSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      handleGetBuyerSuggestion('executives.activity', 'buyers', value, 1, true);
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetSuggestionForActivityBuyer = useMemo(() => debounce(fetchSuggestionsForActivityBuyer, 200), [
    fetchSuggestionsForActivityBuyer,
  ]);

  const fetchNextSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      // Pre-existing bug (also present in BrowseBuyers.js): reads `targets`, which this
      // component never receives as a prop, so `info` is always undefined here. Preserved as-is.
      const info = liveRef.current.targets.getIn(['activity', 'buyers', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerSuggestion('executives.activity', 'buyers', value, currentPage + 1, true);
      }
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetNextSuggestionForActivityBuyer = useMemo(
    () => debounce(fetchNextSuggestionsForActivityBuyer, 200),
    [fetchNextSuggestionsForActivityBuyer],
  );

  const fetchSuggestionsForNextAction = useCallback(
    ({ value }) => {
      const suggestions = liveRef.current.allActivitiesWithoutDuplicates.filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'executives',
        filterName: 'nextAction',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForNextAction = useMemo(() => debounce(fetchSuggestionsForNextAction, 200), [
    fetchSuggestionsForNextAction,
  ]);

  const fetchSuggestionsForActivity = useCallback(
    ({ value }) => {
      const suggestions = (liveRef.current.activities ?? List()).filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'executives',
        filterName: 'activity',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForActivity = useMemo(() => debounce(fetchSuggestionsForActivity, 200), [
    fetchSuggestionsForActivity,
  ]);

  const fetchSuggestionsForStates = useCallback(
    ({ value }) => {
      const suggestions = states.filter(
        state =>
          state
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'executives',
        filterName: 'companyState',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForStates = useMemo(() => debounce(fetchSuggestionsForStates, 200), [
    fetchSuggestionsForStates,
  ]);

  const handleSubCheckboxChange = useCallback(
    (filterName, data) => {
      handleUpdateFilter({
        name: 'executives',
        filterName,
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleQualitySubCheckboxChange = useCallback(
    data => {
      handleSubCheckboxChange('quality', data);
    },
    [handleSubCheckboxChange],
  );

  const handleInterestSubCheckboxChange = useCallback(
    data => {
      handleSubCheckboxChange('interest', data);
    },
    [handleSubCheckboxChange],
  );

  const handleRelationshipSubCheckboxChange = useCallback(
    data => {
      handleSubCheckboxChange('relationship', data);
    },
    [handleSubCheckboxChange],
  );

  const handleSubFilterChange = useCallback(
    data => {
      handleUpdateExecutivesFundFilter(data);
    },
    [handleUpdateExecutivesFundFilter],
  );

  const handleSubActivityFilterChange = useCallback(
    data => {
      handleUpdateExecutivesActivityFilter(data);
    },
    [handleUpdateExecutivesActivityFilter],
  );

  const fetchFundSuggestionsForFund = useCallback(
    ({ value }) => {
      handleGetFundSuggestion(value);
    },
    [handleGetFundSuggestion],
  );
  const handleGetFundSuggestionForFund = useMemo(() => debounce(fetchFundSuggestionsForFund, 200), [
    fetchFundSuggestionsForFund,
  ]);

  const fetchNextFundSuggestionsForFund = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['fund', 'fund', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetFundSuggestion(value, currentPage + 1);
      }
    },
    [handleGetFundSuggestion],
  );
  const handleGetNextFundSuggestionForFund = useMemo(() => debounce(fetchNextFundSuggestionsForFund, 100), [
    fetchNextFundSuggestionsForFund,
  ]);

  const fetchProjectSuggestionsForFund = useCallback(
    ({ value }) => {
      const fundSelected = liveRef.current.executives.getIn(['fund', 'fund', 'selected']);

      if (fundSelected) {
        handleGetFundProjectSuggestion(fundSelected.get('id'), value);
      }
    },
    [handleGetFundProjectSuggestion],
  );
  const handleGetProjectSuggestionForFund = useMemo(() => debounce(fetchProjectSuggestionsForFund, 200), [
    fetchProjectSuggestionsForFund,
  ]);

  const fetchNextProjectSuggestionsForFund = useCallback(
    ({ value }) => {
      const info = liveRef.current.executives.getIn(['fund', 'project', 'pagination']);
      const currentPage = info.get('currentPage', null);
      const fundSelected = liveRef.current.executives.getIn(['fund', 'fund', 'selected']);

      if (fundSelected && currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetFundProjectSuggestion(fundSelected.get('id'), value, currentPage + 1);
      }
    },
    [handleGetFundProjectSuggestion],
  );
  const handleGetNextProjectSuggestionForFund = useMemo(
    () => debounce(fetchNextProjectSuggestionsForFund, 100),
    [fetchNextProjectSuggestionsForFund],
  );

  const handleDownloadExport = useCallback(
    (idList, params) => {
      const url = `${config.API_BASE_URL}/api/v1/browse/executives/export`;

      if (idList.length > SAFE_EXPORT_EXECUTIVES_NUMBER) {
        return handleExportWithSearchCriteria(url, idList, params);
      }

      downloadBrowseFile(url, {
        selected_ids: idList,
        hash: params.hash,
      });
    },
    [handleExportWithSearchCriteria],
  );

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/contact/${row.data.id}`, '_blank');
  }, []);

  const handleToggle = useCallback(() => {
    updateTabVisibility({
      field: 'executives.showTabControls',
      value: !executives.get('showTabControls'),
    });
  }, [executives, updateTabVisibility]);

  const handleFilterClear = useCallback(
    name => {
      handleClearFilter({ name });
    },
    [handleClearFilter],
  );

  const handleRequestAbort = useCallback(
    name => {
      handleAbortRequest(name);
    },
    [handleAbortRequest],
  );

  useEffect(() => {
    onFetchStatuses();
    onFetchEventActivities();

    const searchParams = new URLSearchParams(location.search);
    const criteria = searchParams.get('criteria');

    if (criteria) {
      const payload = {
        name: 'executives',
        hash: criteria,
      };

      handleFetchCriteria(payload);
      setSearchParamsHash(payload);
    }

    if (!hasPageAccess) {
      showError(openPopup, config.permisionError, () => {
        closePopup();
      });
    }
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  useEffect(() => {
    const newSortModel = executives.getIn(['sortBy', 'sortModel']);

    if (hasMountedRef.current) {
      if (newSortModel !== previousSortModelRef.current) {
        const requestParam = buildRequestParam(executives);

        if (!isEmpty(requestParam)) {
          const sortBy = buildSortByParam(executives);

          handleBrowse({
            name: 'executives',
            params: requestParam,
            page: 1,
            sortBy,
            keepSelection: keepSelectionRef.current,
          });
        }
      }
    } else {
      hasMountedRef.current = true;
    }

    previousSortModelRef.current = newSortModel;
  }, [executives, handleBrowse]);

  const controlClassName = classNames(style.controls, { [style.controlsHidden]: !executives.get('showTabControls') });

  const params = {
    requestParams: buildRequestParam(executives),
    stateParams: buildFiltersStateParam(executives),
  };

  const hasParams = !isEmpty(params.requestParams);

  return (
    <div className="tab-pane active" id="browse-executives" role="tabpanel">
      {hasPageAccess && (
        <div className={style.inner}>
          <BrowseTable
            columnDefs={columnDefs}
            contextActionList={contextActionList}
            data={executives}
            hasParams={hasParams}
            location={location}
            name="executives"
            onAbortRequest={handleRequestAbort}
            onAddTag={handleExecutivesAddTag}
            onChange={handleChange}
            onClearFilter={handleFilterClear}
            onDelete={handleTargetDelete}
            onDoubleClickRow={handleDoubleClickRow}
            onDownloadExport={handleDownloadExport}
            onGetNextPageData={handleGetNextPageData}
            onGetPageData={handleGetNextPageData}
            onPushNextActionsDates={handleTargetPushNextActionsDates}
            onSaveAddUpdateBuyer={handleSaveAddUpdateBuyer}
            onSearchParamsBeforeBrowse={handleSearchParamsBeforeBrowse}
            onSelectRows={massSelectRows}
            onSetNextActions={handleTargetSetNextActions}
            onToggle={handleToggle}
            onUpdateQueryResult={handleUpdateQueryResult}
            requestParams={params.requestParams}
            stateParams={params.stateParams}
            user={user}
            {...rest}
          />
          <Card className={controlClassName} title="Filters">
            <BrowseStatusesFilter
              filter={executivesFilter.get('currentStatus')}
              info={executives.get('currentStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseStatusesFilter
              filter={executivesFilter.get('highStatus')}
              info={executives.get('highStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseAutoCompleteTag
              filter={executivesFilter.get('module')}
              info={executives.get('module')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForModule}
              onGetSuggestion={handleGetSuggestionForModule}
            />
            <BrowseAutoCompleteTag
              filter={executivesFilter.get('analyst')}
              info={executives.get('analyst')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForAnalyst}
              onGetSuggestion={handleGetSuggestionForAnalyst}
            />
            <BrowseAutoCompleteTag
              filter={executivesFilter.get('dealmaker')}
              info={executives.get('dealmaker')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForDealmaker}
              onGetSuggestion={handleGetSuggestionForDealmaker}
            />
            <BrowseAutoCompleteTag
              filter={executivesFilter.get('researcher')}
              info={executives.get('researcher')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForResearcher}
              onGetSuggestion={handleGetSuggestionForResearcher}
            />
            <BrowseFundFilter
              filter={executivesFilter.get('fund')}
              info={executives.get('fund')}
              onChange={handleChange}
              onGetFundSuggestion={handleGetFundSuggestionForFund}
              onGetNextFundSuggestion={handleGetNextFundSuggestionForFund}
              onGetNextProjectSuggestion={handleGetNextProjectSuggestionForFund}
              onGetProjectSuggestion={handleGetProjectSuggestionForFund}
              onSubFilterChange={handleSubFilterChange}
            />
            <BrowseInputTag
              filter={executivesFilter.get('harvcoTags')}
              info={executives.get('harvcoTags')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseInputTag
              filter={executivesFilter.get('industry')}
              info={executives.get('industry')}
              onChange={handleChange}
              isBIC
              submitOnEnter
            />
            <BrowseActionFilter
              filter={executivesFilter.get('nextAction')}
              info={executives.get('nextAction')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForNextAction}
            />
            <BrowseActivityFilter
              buyersFilter={executivesFilter.getIn(['activity', 'buyers'])}
              buyersInfo={executives.getIn(['activity', 'buyers'])}
              filter={executivesFilter.get('activity')}
              info={executives.get('activity')}
              onChange={handleChange}
              onGetActivityUserSuggestion={handleGetSuggestionForActivityUser}
              onGetBuyerSuggestion={handleGetSuggestionForActivityBuyer}
              onGetNextActivityUserSuggestion={handleGetNextSuggestionForActivityUser}
              onGetNextBuyerSuggestion={handleGetNextSuggestionForActivityBuyer}
              onGetSuggestion={handleGetSuggestionForActivity}
              onSubActivityFilterChange={handleSubActivityFilterChange}
              projectTypeFilter={executivesFilter.get('projectType')}
              projectTypeInfo={executives.getIn(['activity', 'projectType'])}
              statuses={statuses}
              usersFilter={executivesFilter.getIn(['activity', 'users'])}
              usersInfo={executives.getIn(['activity', 'users'])}
            />
            <BrowseInputTag
              filter={executivesFilter.get('areaCode')}
              info={executives.get('areaCode')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseAutoCompleteTag
              filter={executivesFilter.get('companyState')}
              info={executives.get('companyState')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForStates}
            />
            <BrowseInputTag
              filter={executivesFilter.get('keywords')}
              info={executives.get('keywords')}
              onChange={handleChange}
              shouldGroupTags
              submitOnEnter
            />
            <BrowseDropDown
              filter={executivesFilter.get('resume')}
              info={executives.get('resume')}
              onChange={handleChange}
            />
            <BrowseDropDown filter={executivesFilter.get('mbo')} info={executives.get('mbo')} onChange={handleChange} />
            <BrowseDropDown
              filter={executivesFilter.get('corpBuyer')}
              info={executives.get('corpBuyer')}
              onChange={handleChange}
            />
            <BrowseDropDown
              filter={executivesFilter.get('needsFund')}
              info={executives.get('needsFund')}
              onChange={handleChange}
            />
            <BrowseDropDown
              filter={executivesFilter.get('peExperience')}
              info={executives.get('peExperience')}
              onChange={handleChange}
            />
            <BrowseDropDown
              filter={executivesFilter.get('events')}
              info={executives.get('events')}
              onChange={handleChange}
            />
            <BrowseDropDown
              filter={executivesFilter.get('meet')}
              info={executives.get('meet')}
              onChange={handleChange}
            />
            <BrowseCheckbox
              filter={executivesFilter.get('quality')}
              info={executives.get('quality')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'wcm'])}
                  info={executives.getIn(['quality', 'wcm'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'solid'])}
                  info={executives.getIn(['quality', 'solid'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'good'])}
                  info={executives.getIn(['quality', 'good'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'goodNotVerified'])}
                  info={executives.getIn(['quality', 'goodNotVerified'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'average'])}
                  info={executives.getIn(['quality', 'average'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'weaker'])}
                  info={executives.getIn(['quality', 'weaker'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'poor'])}
                  info={executives.getIn(['quality', 'poor'])}
                  onChange={handleQualitySubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['quality', 'unsure'])}
                  info={executives.getIn(['quality', 'unsure'])}
                  onChange={handleQualitySubCheckboxChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseCheckbox
              filter={executivesFilter.get('interest')}
              info={executives.get('interest')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['interest', 'very'])}
                  info={executives.getIn(['interest', 'very'])}
                  onChange={handleInterestSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['interest', 'somewhat'])}
                  info={executives.getIn(['interest', 'somewhat'])}
                  onChange={handleInterestSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['interest', 'no'])}
                  info={executives.getIn(['interest', 'no'])}
                  onChange={handleInterestSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['interest', 'unknown'])}
                  info={executives.getIn(['interest', 'unknown'])}
                  onChange={handleInterestSubCheckboxChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseCheckbox
              filter={executivesFilter.get('relationship')}
              info={executives.get('relationship')}
              onChange={handleChange}
            >
              <BrowseControlContent variant="checkbox">
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['relationship', 'strong'])}
                  info={executives.getIn(['relationship', 'strong'])}
                  onChange={handleRelationshipSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['relationship', 'semi'])}
                  info={executives.getIn(['relationship', 'semi'])}
                  onChange={handleRelationshipSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['relationship', 'brief'])}
                  info={executives.getIn(['relationship', 'brief'])}
                  onChange={handleRelationshipSubCheckboxChange}
                />
                <BrowseCheckbox
                  filter={executivesFilter.getIn(['relationship', 'no'])}
                  info={executives.getIn(['relationship', 'no'])}
                  onChange={handleRelationshipSubCheckboxChange}
                />
              </BrowseControlContent>
            </BrowseCheckbox>
            <BrowseDropDown
              filter={executivesFilter.get('doNotMail')}
              info={executives.get('doNotMail')}
              onChange={handleChange}
            />
            <BrowseDropDown
              filter={executivesFilter.get('employed')}
              info={executives.get('employed')}
              onChange={handleChange}
            />
          </Card>
        </div>
      )}
    </div>
  );
};

BrowseExecutives.propTypes = {
  handleUpdateExecutivesActivityFilter: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    executives: state.browse.get('executives'),
    statuses: state.statuses.get('executiveStatuses'),
    user: state.auth.get('user'),
    activities: state.eventActivities.getIn(['activities', 'all', 'exec']),
    allActivitiesWithoutDuplicates: state.eventActivities.get('allActivitiesWithoutDuplicates'),
  };
}

const mapDispatchToProps = dispatch => ({
  onFetchStatuses: () => dispatch(fetchExecutivesStatuses()),
  onFetchEventActivities: () => dispatch(fetchEventActivities()),
  ...bindActionCreators(browseActionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(BrowseExecutives));
